<template>
  <div class="main__booking-summary container">
    <div ref="formRoot" class="summary">
      <div class="summary__header">
        <img :src="this.cruiseBannerUrl" />
        <div class="summary__header__info">
          <el-row justify="space-between" type="flex">
            <div class="summary__cruise-name">
              {{ cabinConfigurations[0] ? cabinConfigurations[0].ship : "" }}
            </div>
            <div class="summary__booking-number">
              <div class="summary__booking-number__label">Booking number</div>
              <div class="summary__booking-number__value">
                {{ booking.bookingCode }}
              </div>
            </div>
          </el-row>

          <el-row justify="space-between" style="margin-top: 32px" type="flex">
            <div v-for="summary in bookingSummary" class="summary__card">
              <i :class="'icon ' + summary.iconClass"></i>
              <div class="summary__card__label">{{ summary.label }}</div>
              <div class="summary__card__value">{{ summary.value }}</div>
            </div>
          </el-row>
        </div>
      </div>
      <div class="summary__content">
        <div class="summary__content__title">Booking Details</div>
        <el-row
          v-for="(cc, index) in cabinConfigurations"
          :key="cc.cabinConfigurationID"
          class="cabin-configuration"
        >
          <el-col :span="8">
            <div class="cabin-configuration__label">Guest(s)</div>
            <div
              v-for="guestName in getGuestNameList(cc)"
              class="cabin-configuration__guest"
            >
              {{ guestName }}
            </div>
          </el-col>
          <el-col :span="8">
            <div class="cabin-configuration__label">Suite</div>
            <div class="cabin-configuration__suite">
              <i class="icon icon-design-suite" />{{ cc.suite }}
              {{ getCabinCategory(cc) }}
            </div>
            <div v-if="canShowDeck(cc.deck)" class="cabin-configuration__suite">
              <i class="icon icon-deck" />{{ cc.deck }}
            </div>
            <div v-if="canShowBalconyType()" class="cabin-configuration__suite">
              <i class="icon icon-balcony" />{{
                cc.hasBalcony ? "Balcony" : "Non-balcony"
              }}
            </div>
            <div
              v-if="cc.interconnectingCabin && canShowInterconnectingCabin()"
              class="cabin-configuration__suite"
            >
              <i class="icon icon-interconnecting" />Interconnecting with Suite
              {{ cc.interconnectingCabin }}
            </div>
          </el-col>
          <el-col v-if="isBedConfigurable(cc)" :span="8">
            <div class="cabin-configuration__label">Bed configuration</div>
            <el-form :model="ruleForm" ref="ruleFormCabinConfig" :rules="rules">
              <el-form-item :prop="'cabinInput' + index">
                <v-select
                  :clearable="false"
                  :options="
                    cc.hasBalcony
                      ? selectBedConfigurationsForBalcony
                      : cc.ship === 'Aqua Lares'
                        ? selectBedConfigurationsForAquaLares[cc.cabin]
                        : selectBedConfigurationsForNonBalcony
                  "
                  :reduce="(item) => item.value"
                  :searchable="false"
                  :value="ruleForm['cabinInput' + index]"
                  filterable
                  label="label"
                  @input="
                    onCabinConfigurationInputChange(
                      cc.cabinConfigurationID,
                      'cabinBedding',
                      $event,
                      index,
                    )
                  "
                >
                </v-select>
              </el-form-item>
            </el-form>
          </el-col>
          <el-col v-else :span="8">
            <div class="cabin-configuration__label">Bed configuration</div>
            <div class="cabin-configuration__guest">
              {{ getFixedCabinBedding(cc) }} <br />
              {{ getFixedCabinBeddingSize(cc) }}
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <div class="box-note">
      Note: Please ensure that all guest(s) above are confirmed. If any of the
      guest(s) are not able to take the cruise anymore, please
      <a :href="appendMarketingSiteUrl('/contact-us')" target="_blank"
        >contact us</a
      >
      directly to make changes to your guest list.
    </div>

    <el-row class="bottom-nav" justify="end" type="flex">
      <autosave-indicator></autosave-indicator>
      <el-button
        :disabled="isSaving"
        class="button--tertiary bottom-nav__secondaryButton"
        @click="onClickSaveAndExit(false)"
        >Save
      </el-button>
      <el-button
        :disabled="isSaving"
        class="button--primary bottom-nav__primaryButton"
        @click="onClickNext()"
        >Next</el-button
      >
    </el-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import types from "../store/types";
import contactMixin from "../mixins/contact";
import {
  BED_CONFIGURATIONS_FOR_AQUA_LARES,
  BED_CONFIGURATIONS_FOR_BALCONY,
  BED_CONFIGURATIONS_FOR_NON_BALCONY,
  FACILITY_CODES,
  UNHANDLED_ERROR_MESSAGE,
} from "../constants";
import { timeTrack } from "../mixins/timeTrack";
import autosave from "../mixins/autosave";
import AutosaveIndicator from "./AutosaveIndicator";

export default {
  mixins: [contactMixin, timeTrack, autosave],
  components: { AutosaveIndicator },
  mounted() {
    this.initializeAutosave(
      this.onClickSaveAndExit.bind(this),
      this.$refs.formRoot,
    );
  },
  created() {
    gtag("set", "page_path", "/step-1");
    gtag("event", "page_path", {
      path: "/step-1",
    });
    gtag("event", "vessel_type", {
      vessel: this.cabinConfigurations[0]
        ? this.cabinConfigurations[0].ship
        : "",
    });
  },
  data() {
    var validateConfigs = (rule, value, callback) => {
      if (!value) {
        callback(
          new Error(
            "Please specify the desired bed configuration for your stay.",
          ),
        );
      } else {
        callback();
      }
    };

    let dynamicRules = {};
    let dynamicConfigs = {};

    const cabinConfigurations = this.$store.getters["cabinConfigurations"];
    cabinConfigurations.forEach((cabinConfig, index) => {
      dynamicRules["cabinInput" + index] = {
        validator: validateConfigs,
        trigger: "blur",
      };
      dynamicConfigs["cabinInput" + index] = cabinConfig.cabinBedding;
    });

    return {
      selectBedConfigurationsForNonBalcony: BED_CONFIGURATIONS_FOR_NON_BALCONY,
      selectBedConfigurationsForBalcony: BED_CONFIGURATIONS_FOR_BALCONY,
      selectBedConfigurationsForAquaLares: BED_CONFIGURATIONS_FOR_AQUA_LARES,
      ruleForm: dynamicConfigs,
      rules: dynamicRules,
      // formValidate: {
      //   bookingCode: '324137',
      //   contactName: 'Test Agency Lee',
      // },
      // ruleValidate: {
      //   'bookingCode': [
      //     { required: true, message: 'Please enter your booking number', trigger: 'change keyup' }
      //   ],
      //   'contactName': [
      //     { required: true, message: 'Please enter your last / family name', trigger: 'change keyup' },
      //   ],
      // }
    };
  },
  // computed: (() => (
  //   mapSetter(
  //     mapState('login', {
  //       bookingCode: state => state.bookingCode,
  //       contactName: state => state.contactName,
  //     }),
  //     {
  //       bookingCode: function(bookingCode) { this.setBookingCode({ bookingCode })},
  //       contactName: function(contactName) { this.setContactName({ contactName })}
  //     }
  //   )
  // ))(),
  computed: {
    ...mapGetters([
      types.getters.cabinConfigurations,
      types.getters.booking,
      types.getters.isSaving,
    ]),
    cruiseBannerUrl() {
      return `https://d20knvk822eu5a.cloudfront.net/s3fs-public-optimized/static-assets/online-check-in/cruise-banner/cruise-banner-${this.booking.facilityCode}.jpg.webp`;
    },
    bookingSummary: function () {
      return [
        {
          iconClass: "icon-departure",
          label: "Departure date",
          value: this.booking.eventStartDateDisplay,
        },
        {
          iconClass: "icon-arrival",
          label: "Arrival date",
          value: this.booking.eventEndDateDisplay,
        },
        {
          iconClass: "icon-night",
          label: "Nights",
          value: this.booking.eventDurationNumberDays,
        },
        {
          iconClass: "icon-suite",
          label: "No. of suites",
          value: this.cabinConfigurations.length,
        },
        {
          iconClass: "icon-port",
          label: "Departure port",
          value: this.booking.departurePort,
        },
        {
          iconClass: "icon-port",
          label: "Arrival port",
          value: this.booking.arrivalPort,
        },
      ];
    },
  },
  methods: {
    // ...mapActions('booking', [
    //   'setBookingCode', // map `this.setBookingCode(bookingCode)` to `this.$store.dispatch('setBookingCode', bookingCode)`
    //   'setContactName',
    // ]),
    ...mapActions([
      types.actions.updateCabinConfigurationField,
      types.actions.updateCabinConfigurationRemote,
      types.actions.setIsVisibleDialogSaveAndExit,
    ]),
    appendMarketingSiteUrl(path) {
      return `${process.env.MARKETING_SITE_FRONTEND_URL}${path || ""}`;
    },
    canShowBalconyType: function () {
      return (
        !this.booking.isAmazonCruise &&
        this.booking.facilityCode !== FACILITY_CODES.AquaBlu &&
        this.booking.facilityCode !== FACILITY_CODES.AquaMare
      );
    },
    canShowInterconnectingCabin: function () {
      return (
        this.booking.facilityCode !== FACILITY_CODES.AquaMare ||
        this.booking.facilityCode !== FACILITY_CODES.AquaBlu
      );
    },
    canShowDeck: function (deck) {
      return deck && deck !== "";
    },
    getCabinCategory: function (cabinConfiguration) {
      if (
        this.booking.facilityCode !== FACILITY_CODES.AquaBlu &&
        this.booking.facilityCode !== FACILITY_CODES.AquaMare
      ) {
        if (
          this.booking.facilityCode === FACILITY_CODES.AriaAmazon ||
          this.booking.facilityCode === FACILITY_CODES.AquaMekong
        ) {
          if (
            cabinConfiguration.category &&
            cabinConfiguration.category === "TOURESCORT"
          ) {
            return "Tour Escort";
          }
        }

        return "";
      }
      if (cabinConfiguration.category) {
        if (this.booking.facilityCode === FACILITY_CODES.AquaMare) {
          if (cabinConfiguration.category === "CTGOS") {
            return `(${cabinConfiguration.category.replace(/CTGOS/g, "Cat I")})`;
          }
          return `(${cabinConfiguration.category.replace(/CTG/g, "Cat ")})`;
        }
        return `(${cabinConfiguration.category.replace(/Category/g, "Cat")})`;
      }
      return "";
    },
    getFixedCabinBedding: function (cabinConfiguration) {
      const cabinCategory = this.getCabinCategory(cabinConfiguration);
      const cabinNumber = cabinConfiguration.cabin;
      if (this.booking.facilityCode === FACILITY_CODES.AquaBlu) {
        if (["202", "204"].indexOf(cabinNumber) >= 0) {
          return "Twin Bed";
        }

        switch (cabinCategory) {
          case "Cat I":
          case "(Cat I)":
          case "Category I":
            return "King Size Bed";
          case "Cat II":
          case "(Cat II)":
          case "Category II":
            return "King Size Bed";
          case "Cat III":
          case "(Cat III)":
          case "Category III":
            return "Twin Bed";
        }
      }
      if (this.booking.facilityCode === FACILITY_CODES.AquaMare) {
        switch (cabinCategory) {
          case "Cat I":
          case "(Cat I)":
          case "Category I":
            return "King Bed";
          case "Cat II":
          case "(Cat II)":
          case "Category II":
            return "King Bed";
          case "Cat III":
          case "(Cat III)":
          case "Category III":
            return "Double Bed";
          case "Cat IV":
          case "(Cat IV)":
          case "Category IV":
            switch (cabinNumber) {
              case "103":
                return "Twin Bed";
              case "104":
                return "Double Bed";
            }
          case "Cat V":
          case "(Cat V)":
          case "Category V":
            return "Double Bed";
        }
      }
      if (this.booking.facilityCode === FACILITY_CODES.AquaLares) {
        switch (cabinNumber) {
          case "303":
            return "King Size Bed + Sofa Bed";
          case "101":
          case "401":
          case "201":
            return "King Size Bed";
          case "203":
          case "204":
          case "205":
          case "206":
            return "Double Bed";
          case "102":
          case "202":
            return "Twin Bed";
          case "103":
            return "Twin Bed + Bunk Bed";
        }
      }
      return "King Bed";
    },
    getFixedCabinBeddingSize: function (cabinConfiguration) {
      const cabinCategory = this.getCabinCategory(cabinConfiguration);
      const cabinNumber = cabinConfiguration.cabin;
      if (this.booking.facilityCode === FACILITY_CODES.AquaMare) {
        switch (cabinCategory) {
          case "Cat I":
          case "(Cat I)":
          case "Category I":
            return "(200cm x 200cm)";
          case "Cat II":
          case "(Cat II)":
          case "Category II":
            return "(200cm x 200cm)";
          case "Cat III":
          case "(Cat III)":
          case "Category III":
            switch (cabinNumber) {
              case "101":
                return "(160cm x 200cm)";
              case "102":
                return "(170cm x 200cm)";
            }
          case "Cat IV":
          case "(Cat IV)":
          case "Category IV":
            switch (cabinNumber) {
              case "103":
                return "(100cm x 200cm)";
              case "104":
                return "(150cm x 200cm)";
            }
          case "Cat V":
          case "(Cat V)":
          case "Category V":
            return "(140cm x 190cm)";
        }
      }
      return "";
    },
    getGuestNameList: function (cabinConfiguration) {
      return [
        cabinConfiguration.guest1Name,
        cabinConfiguration.guest2Name,
        cabinConfiguration.guest3Name,
        cabinConfiguration.guest4Name,
      ].filter((guestName) => guestName);
    },

    onCabinConfigurationInputChange: function (
      cabinConfigurationID,
      fieldName,
      event,
      index,
    ) {
      console.log(
        "[cabinConfigurationID, fieldName, event]",
        cabinConfigurationID,
        fieldName,
        event,
      );

      this[types.actions.updateCabinConfigurationField]({
        cabinConfigurationID,
        fieldName,
        value: event,
      });

      // Manually set form for dynamic validation
      this.ruleForm["cabinInput" + index] = event;
    },

    async onClickSaveAndExit(isAutosaving = false) {
      try {
        const res = await this.saveDataToRemote(isAutosaving);
        if (isAutosaving) {
          return res;
        } else {
          this.setIsVisibleDialogSaveAndExit(true);
        }
      } catch (error) {
        this.$message.error(error.message || UNHANDLED_ERROR_MESSAGE);
        return false;
      }

      return true;
    },

    onClickNext: function () {
      this.saveDataToRemote()
        .then(() => {
          this.$router.push("step-2");
        })
        .catch((error) => {
          this.$message.error(error.message || UNHANDLED_ERROR_MESSAGE);
        });
    },
    saveDataToRemote(isAutosave = false) {
      const { cabinConfigurations } = this;

      const emptyCabinConfigurations = cabinConfigurations.filter(
        (cabinConfiguration) => {
          return (
            this.isBedConfigurable(cabinConfiguration) &&
            !cabinConfiguration.cabinBedding
          );
        },
      );

      if (emptyCabinConfigurations.length) {
        if (!isAutosave) {
          // run validate on each cabin configuration form
          this.$refs.ruleFormCabinConfig.forEach((form) => {
            form.validate();
          });
        }
        return false;
      }

      // Update bed configurations for cabins without configurable beds
      cabinConfigurations.forEach((cabinConfiguration) => {
        if (!this.isBedConfigurable(cabinConfiguration)) {
          this[types.actions.updateCabinConfigurationField]({
            cabinConfigurationID: cabinConfiguration.cabinConfigurationID,
            fieldName: "cabinBedding",
            value: this.getFixedCabinBedding(cabinConfiguration),
          });
        }
      });

      return this[types.actions.updateCabinConfigurationRemote]({
        cabinConfigurations,
        showLoader: !isAutosave,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../scss/color";
@import "../scss/typography";

.summary {
  color: $text-normal-color;

  &__header {
    position: relative;
    height: 600px;
    margin-bottom: 24px;

    img {
      width: 100%;
      height: 600px;
      object-fit: cover;
      border-radius: 4px;
    }

    &__info {
      padding: 40px 40px 24px 40px;
      background-color: $aqua-grey-100;
      position: absolute;
      bottom: 24px;
      left: 24px;
      right: 24px;
      margin: 0 auto;
      border-radius: 4px;
    }
  }

  &__cruise-name {
    @include desktop-title-title4;
    color: $white-100;
  }

  &__booking-number {
    text-align: right;

    &__label {
      @include global-paragraph-small-regular;
      color: $white-100;
    }

    &__value {
      margin-top: 2px;
      @include global-paragraph-x-large-bold;
      color: $white-100;
    }
  }

  &__card {
    position: relative;
    padding: 12px 24px 12px 56px;
    background-color: rgba(255, 255, 255, 0.25);
    border-radius: 4px;

    i {
      left: 8px;
      top: 50%;
      transform: translateY(-50%);
    }

    &__label {
      @include global-paragraph-small-regular;
      color: $aqua-grey-25;
    }

    &__value {
      margin-top: 4px;
      @include global-paragraph-large-bold;
      color: $white-100;
    }
  }

  &__content {
    padding: 40px 40px 24px 40px;
    background-color: #fff;
    border: 1px solid $aqua-grey-75;
    border-radius: 4px;

    &__title {
      @include global-paragraph-large-bold;
      color: $aqua-grey-100;
      border-bottom: 1px solid $line-color;
      padding-bottom: 16px;
    }
  }
}

.cabin-configuration {
  margin-top: 32px;
  padding: 24px 0;

  .el-col {
    padding-left: 32px;
    padding-right: 32px;
    border-left: 1px solid $line-color;
    border-right: 1px solid $line-color;

    &:first-child {
      padding-left: 0;
      margin-left: 1px;
      margin-right: -1px;
      border-left: 0;
    }

    &:last-child {
      padding-right: 0;
      margin-left: -1px;
      margin-right: 1px;
      border-right: 0;
    }
  }

  &__label {
    margin-bottom: 8px;
    @include global-paragraph-medium-regular;
    color: $aqua-grey-90;
  }

  &__guest {
    margin-bottom: 8px;
    @include global-paragraph-medium-bold;
    color: $aqua-grey-100;
  }

  &__suite {
    position: relative;
    margin-bottom: 6px;
    padding-left: 32px;

    @include global-paragraph-medium-regular;
    color: $aqua-grey-100;

    i {
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.box-note {
  margin-top: 16px;
  padding: 16px 40px;
  background-color: #fff;
  @include global-paragraph-medium-regular;
  letter-spacing: 1px;
  color: $aqua-grey-75;
  border-radius: 4px;
  border: 1px solid $aqua-grey-25;
}

// IE Fix
.bottom-nav {
  display: block \9;
  text-align: right;

  &__primaryButton {
    width: 184px;
  }

  &__secondaryButton {
    width: 184px;
  }
}

.autosave-indicator {
  &__label {
    color: $aqua-grey-100;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BookingSummaryPage.vue